import React, { useContext, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Box, Grid, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import {
  BulkTransferUsersRequest,
  Region,
  SCEmployee,
  TransferUserRequest,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import mainStyles from "../../styles";
import RegionSelector from "./shared/region-selector";
import { bulkTransferUsers } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import ActionPermissionWrapper from "components/shared/action-permission-wrapper";
import CodeBox from "components/shared/code-box";
import DragAndDrop, { FileToReturn } from "components/shared/drag-and-drop";
import { stringToSubscriptionSeatTypesMapping } from "constants/seats";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { parse } from "csv-parse/browser/esm";
import { getScUserEmail } from "helpers/get-sc-user-email";
import { getScUserName } from "helpers/get-sc-user-name";

interface BulkTransferUsersFormProps {
  listSupportWorkflows: () => void;
}

const BulkTransferUsersForm = ({
  listSupportWorkflows,
}: BulkTransferUsersFormProps) => {
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState<FileToReturn[]>([]);
  const [scUserName, setScUserName] = useState("");
  const [scUserEmail, setScUserEmail] = useState("");

  const { showError } = useContext(SnackbarContext);
  const [targetRegion, setTargetRegion] = useState<Region>();

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };
      reader.readAsText(file);
    });
  };

  const processCSVFiles = async (): Promise<TransferUserRequest[]> => {
    const csvFiles = formState.filter((file) =>
      file.type.startsWith("text/csv")
    );
    const usersToAdd: TransferUserRequest[] = [];

    const promises = csvFiles.map(async (file) => {
      const text = await readFileAsText(file);

      return new Promise<void>((resolve, reject) => {
        parse(
          text,
          { skip_empty_lines: true, trim: true },
          (err, output: string[][]) => {
            if (err || output.length === 0 || output[0].length !== 5) {
              let displayError: Error;
              if (err) {
                displayError = new Error("Error parsing CSV:", err);
              } else if (output.length === 0) {
                displayError = new Error("No data found in CSV");
              } else if (output[0].length !== 5) {
                displayError = new Error(
                  `Invalid number of columns [${output[0].length}] csv must be in the format: user ID,target organization ID,target group ID. See the info panel for more details.`
                );
              } else {
                displayError = new Error("Unknown error occurred");
              }
              showError(displayError as GrpcError);
              reject(displayError);
              return;
            }

            if (file.csvHeaders) {
              output.shift(); // Remove headers if necessary
            }

            output.forEach((row) => {
              usersToAdd.push({
                sourceUserId: row[0],
                sourceOrgId: row[1],
                targetUserId: row[2],
                targetOrgId: row[3],
                targetSeatType:
                  stringToSubscriptionSeatTypesMapping[row[4].toLowerCase()],
                region: targetRegion,
              } as TransferUserRequest);
            });

            resolve();
          }
        );
      });
    });

    await Promise.all(promises);

    return usersToAdd;
  };

  useEffect(() => {
    getScUserName().then((name) => setScUserName(name));
    getScUserEmail().then((email) => setScUserEmail(email));
  }, []);

  const transferUsers = async () => {
    setIsLoading(true);

    const usersToTransfer = await processCSVFiles();
    const req = {
      usersToTransfer,
      scEmployee: new SCEmployee({
        name: scUserName,
        email: scUserEmail,
      }),
    } as BulkTransferUsersRequest;

    await bulkTransferUsers(req, callOptionsHeaders(DEFAULT_REGION_CODE))
      .then(() => {
        setFormState([]);
      })
      .then(() => {
        listSupportWorkflows();
      })
      .catch((error) => {
        showError(error as GrpcError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const sampleCSV = `  source_user_id,source_org_id,target_user_id(optional),target_org_id,target_seat_type
  user_cc09330bdbde48069a8f552fbd892db4,role_5b367bc6bee04129a520c6b18343b6da,,role_5b367bc6bee04129a520c6b18343b6da,FULL
  user_a7c43dc569484f08ad0ba8bf7a0c3dc6,role_5b367bc6bee04129a520c6b18343b6da,user_b6276977904542c7a1c53401d2345bf5,role_5b367bc6bee04129a520c6b18343b6da,FULL
  user_b1588a7c68f44d24be9199927edde88a,role_5b367bc6bee04129a520c6b18343b6da,,role_5b367bc6bee04129a520c6b18343b6da,LITE
  user_391fca661bdd443cae6c85d69a4dec18,role_5b367bc6bee04129a520c6b18343b6da,,role_5b367bc6bee04129a520c6b18343b6da,GUEST`;

  const BulkTransferUsersInfoCard = () => {
    return (
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="info-panel-content"
            id="info-panel-header"
            test-dataid="info-panel-summary"
          >
            <HelpIcon color="primary" />
            <Typography
              data-testid="info-panel-title"
              style={{ paddingLeft: ".25em" }}
            >
              How to use: Bulk Transfer Users with Their Documents
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert
              severity="info"
              variant="filled"
              style={{ margin: "2px" }}
              sx={{ justifyContent: "flex-start", textAlign: "left" }}
            >
              <div>
                <div style={{ marginBottom: "8px" }}>
                  You are about to bulk transfer users with their templates and
                  inspections to another org. The source users will be
                  deactivated, and the preferred login for the user identity set
                  to the target org.
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <b>Important:</b> When documents are transferred to another
                  organisation, all access rules are automatically reset to only
                  include the document owner. You will need to manually
                  reconfigure sharing permissions and access rules for templates
                  and inspections after the transfer is complete.
                </div>
                <div>
                  Contact #business-systems slack channel if inspection or
                  template documents contain <b>GRS and/or sites</b>, because{" "}
                  <b>they will be broken</b> after the transfer.
                </div>
              </div>
            </Alert>
            <Typography
              style={{ textAlign: "left" }}
              data-testid="info-panel-description"
            >
              <p>Bulk User Transfer will take in a CSV of this row format:</p>
              <CodeBox codeBlob={sampleCSV} />
              <ul>
                <li>
                  Every organization id should be identical since all users
                  should be transferring to and from the same organizations.
                </li>
                <li>
                  If the source user email already exists in the target org or
                  you only want the user&apos;s documents to be transferred,
                  then you can just transfer the documents by specify a target
                  user id in the target org.
                </li>
              </ul>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <Box sx={{ width: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Upload CSV File with User, Source and Target Org Details
            </Typography>
            <DragAndDrop
              acceptedFileTypes={{ "text/csv": [".csv"] }}
              setDragAndDropFiles={setFormState}
              maxAcceptedFiles={1}
              files={formState}
              setFiles={setFormState}
              removeFilePreview={(index: number) => {
                setFormState((prev) => prev.filter((_, i) => i !== index));
              }}
            />
          </Box>
          <RegionSelector
            targetRegion={targetRegion}
            setTargetRegion={setTargetRegion}
            isLoading={isLoading}
          />
          <ActionPermissionWrapper action={ActionType.BULK_TRANSFER_USERS}>
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                transferUsers();
                setFormState([]);
              }}
              loading={isLoading}
              disabled={formState.length === 0 || targetRegion === undefined}
              data-testid="execute-workflow-button"
            >
              Execute Workflow
            </LoadingButton>
          </ActionPermissionWrapper>
        </Box>
      </Grid>

      <Grid item xs={12} style={{ paddingBottom: "20px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <BulkTransferUsersInfoCard />
        </Grid>
      </Grid>
    </>
  );
};

export default BulkTransferUsersForm;
